/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import {
  string,
  func,
  shape,
  number,
  bool,
} from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import withApplicationConfig from 'containers/withApplicationConfig';
import { Paragraph, Button, Small } from 'components/common';
import { validateDob, isInputNumber } from 'helpers/ValidationUtil';
import './DobPanel.scss';

const INPUT_ENUM = {
  DAY: 'DAY',
  MONTH: 'MONTH',
  YEAR: 'YEAR',
};

class DobPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      day: '',
      month: '',
      year: '',
      isDayInputClean: true,
      isMonthInputClean: true,
      isYearInputClean: true,
      isDayInputFocused: false,
      isMonthInputFocused: false,
      isYearInputFocused: false,
      isValidDate: false,
      errorMessage: null,
    };

    const { initialValue } = props;
    if (initialValue) {
      const dobMoment = moment(initialValue, 'YYYY-MM-DD');
      this.state.day = dobMoment.format('DD');
      this.state.month = dobMoment.format('MM');
      this.state.year = dobMoment.format('YYYY');
      this.state.isValidDate = true;
    }

    this.dayInputRef = React.createRef();
    this.monthInputRef = React.createRef();
    this.yearInputRef = React.createRef();

    this.onChangeHandler = this.onChangeHandler.bind(this);
    this.onBlurHandler = this.onBlurHandler.bind(this);
    this.validate = this.validate.bind(this);
    this.localOnConfirm = this.localOnConfirm.bind(this);
    this.localOnEnter = this.localOnEnter.bind(this);
    this.confirmDate = this.confirmDate.bind(this);
    this.focusOnMount = this.focusOnMount.bind(this);

    this.handleDobOnChange = this.handleDobOnChange.bind(this);
    this.handleBackspace = this.handleBackspace.bind(this);
  }

  componentDidMount() {
    this.focusOnMount();
    this.validate();
  }

  onChangeHandler() {
    const {
      day,
      month,
      year,
      isDayInputFocused,
      isMonthInputFocused,
      isYearInputFocused,
    } = this.state;

    if (isDayInputFocused && (day.length >= 2 || parseInt(day.charAt(0), 10) > 3)) {
      // Restrict input length
      this.setState({ day: (`0${day.substring(0, 2)}`).slice(-2) }, this.validate);
      if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) {
        return;
      }
      // Focus next input
      const { current: monthEl } = this.monthInputRef;
      if (monthEl) monthEl.focus();
      return;
    }

    if (isMonthInputFocused) {
      if (month.length >= 2 || parseInt(month.charAt(0), 10) > 1) {
        // Restrict input length
        this.setState({ month: (`0${month.substring(0, 2)}`).slice(-2) }, this.validate);

        if (parseInt(month, 10) < 1 || parseInt(month, 10) > 12) {
          return;
        }

        // Focus next input
        const { current: yearEl } = this.yearInputRef;
        if (yearEl) yearEl.focus();
        return;
      }

      if (month.length === 0) {
        return;
      }
    }

    if (isYearInputFocused && year.length >= 4) {
      // Restrict input length
      this.setState({ year: year.substring(0, 4) }, this.validate);
      return;
    }

    if (isYearInputFocused && year.length > 0) {
      const millennium = parseInt(year.charAt(0), 10);
      if (millennium !== 1 && millennium !== 2) {
        this.setState({ year: '' });
        return;
      }
    }
    if (isYearInputFocused && year.length === 0) {
      return;
    }

    this.validate();
  }

  onBlurHandler(inputEnum) {
    const {
      day,
      month,
      year,
    } = this.state;
    return () => {
      if (day) this.setState({ isDayInputClean: false });
      if (month) this.setState({ isMonthInputClean: false });
      if (year) this.setState({ isYearInputClean: false });

      switch (inputEnum) {
        case INPUT_ENUM.DAY:
          this.setState({
            isDayInputFocused: false,
          });
          if (day) {
            this.setState({
              day: (`0${day.substring(0, 2)}`).slice(-2),
            });
          }

          break;
        case INPUT_ENUM.MONTH:
          this.setState({
            isMonthInputFocused: false,
          });
          if (month) {
            this.setState({
              month: (`0${month.substring(0, 2)}`).slice(-2),
            });
          }
          break;
        case INPUT_ENUM.YEAR:
          this.setState({
            isYearInputFocused: false,
          });
          break;
        default:
          break;
      }
    };
  }

  confirmDate() {
    const { day, month, year } = this.state;
    const { onConfirm } = this.props;

    // Validation should be done already in validate() method
    // so we don't validate one more time in here

    // For Safari we need to use '/' instead of '-' for date format
    // We need to replace '/' with '-' before invoking OnConfirm
    const dobString = moment(`${year}/${month}/${day}`).format('YYYY/MM/DD');
    if (onConfirm) onConfirm(dobString.split('/').join('-'));
  }

  localOnEnter(e) {
    this.validate();
    const { isValidDate } = this.state;
    if (e.key === 'Enter' && isValidDate) {
      this.confirmDate();
    }
  }

  localOnConfirm() {
    this.confirmDate();
  }

  focusOnMount() {
    const { month, year } = this.state;
    const { current: dayInputEl } = this.dayInputRef;
    const { current: monthInputEl } = this.monthInputRef;
    const { current: yearInputEl } = this.yearInputRef;

    if (year && yearInputEl) {
      yearInputEl.focus();
      return;
    }

    if (month && monthInputEl) {
      monthInputEl.focus();
      return;
    }

    if (dayInputEl) {
      dayInputEl.focus();
    }
  }

  validate() {
    const { day, month, year } = this.state;
    const {
      minAge,
      maxAge,
      // app config
      dobMessages = {},
      onDateOfBirthValidChange,
    } = this.props;

    let dobMoment = moment();
    if ((day.length <= 2 && day) && (month.length <= 2 && month) && (year.length === 4)) {
      dobMoment = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
    }

    const {
      invalidDateErrorMessage,
      dateOutOfRangeErrorMessage,
      dateInFutureErrorMessage,
      dateBeyondReasonableYearsAgoErrorMessage,
    } = dobMessages;

    const result = validateDob(
      dobMoment,
      invalidDateErrorMessage,
      dateOutOfRangeErrorMessage,
      minAge,
      maxAge,
      dateInFutureErrorMessage,
      dateBeyondReasonableYearsAgoErrorMessage,
    );

    this.setState(result);
    if (onDateOfBirthValidChange) onDateOfBirthValidChange(result.isValidDate);
  }

  handleDobOnChange(e) {
    const { type, value, name } = e.target;
    const checkIsNumber = isInputNumber(type, value);

    if (checkIsNumber) {
      switch (name) {
        case 'day':
          this.setState({
            day: value,
          }, this.onChangeHandler);
          break;
        case 'month':
          this.setState({ month: value }, this.onChangeHandler);
          break;
        case 'year':
          this.setState({ year: value }, this.onChangeHandler);
          break;
        default:
      }
    }
  }

  handleBackspace(e) {
    const {
      isMonthInputFocused,
      isYearInputFocused,
    } = this.state;

    if (e.key === 'Backspace') {
      if (isMonthInputFocused) {
        const monthValue = this.monthInputRef.current.value;
        if (monthValue === '') {
          const { current: dayEl } = this.dayInputRef;
          if (dayEl) dayEl.focus();
        }
      } else if (isYearInputFocused) {
        const yearValue = this.yearInputRef.current.value;
        if (yearValue === '') {
          const { current: monthEl } = this.monthInputRef;
          if (monthEl) monthEl.focus();
        }
      }
    }
  }

  render() {
    const {
      day,
      month,
      year,
      isValidDate,
      errorMessage,
      isDayInputClean,
      isMonthInputClean,
      isYearInputClean,
    } = this.state;

    const {
      // App config
      dobPanel,
      isForcedShowError,
      dataSelectorId,
      dataSelectorValue,
    } = this.props;

    return (
      <div className="dob-panel" data-selector={dataSelectorId} data-selector-value={dataSelectorValue}>
        <div className="title">
          <Paragraph secondary>
            <strong>{dobPanel.title}</strong>
          </Paragraph>
        </div>
        <div className="dob-panel-input-group" onKeyPress={this.localOnEnter}>
          <input
            ref={this.dayInputRef}
            type="tel"
            placeholder="DD"
            name="day"
            value={day}
            onChange={this.handleDobOnChange}
            onFocus={() => this.setState({ isDayInputFocused: true })}
            onBlur={this.onBlurHandler(INPUT_ENUM.DAY)}
            autoComplete="off"
            data-selector="day"
          />
          <span className="slash">/</span>
          <input
            ref={this.monthInputRef}
            type="tel"
            placeholder="MM"
            name="month"
            value={month}
            onKeyDown={this.handleBackspace}
            onChange={this.handleDobOnChange}
            onFocus={() => this.setState({ isMonthInputFocused: true })}
            onBlur={this.onBlurHandler(INPUT_ENUM.MONTH)}
            autoComplete="off"
            data-selector="month"
          />
          <span className="slash">/</span>
          <input
            ref={this.yearInputRef}
            type="tel"
            placeholder="YYYY"
            className="year"
            name="year"
            value={year}
            onKeyDown={this.handleBackspace}
            onChange={this.handleDobOnChange}
            onFocus={() => this.setState({ isYearInputFocused: true })}
            onBlur={this.onBlurHandler(INPUT_ENUM.YEAR)}
            autoComplete="off"
            data-selector="year"
          />
        </div>
        {errorMessage
          && ((!isDayInputClean && !isMonthInputClean && !isYearInputClean) || isForcedShowError)
          && (
            <div className="error-message-wrapper">
              <Small dataSelectorId="error">
                {errorMessage}
              </Small>
            </div>
          )}
        <div className="done-button-wrapper">
          <Button
            text
            onClick={this.localOnConfirm}
            disabled={!isValidDate}
            dataSelectorId="done"
          >
            {_.get(dobPanel, 'dictionary.done')}
          </Button>
        </div>
      </div>
    );
  }
}

DobPanel.propTypes = {
  initialValue: string,
  onConfirm: func,
  onDateOfBirthValidChange: func,
  minAge: number,
  maxAge: number,
  isForcedShowError: bool,

  // App config
  dobPanel: shape({}),
  dobMessages: shape({}),
  dataSelectorId: string,
  dataSelectorValue: string,
};

DobPanel.defaultProps = {
  initialValue: '',
  onConfirm: undefined,
  onDateOfBirthValidChange: undefined,
  minAge: 18,
  maxAge: 100,
  isForcedShowError: false,

  // App config
  dobPanel: {},
  dobMessages: {},
  dataSelectorId: null,
  dataSelectorValue: null,
};

export default withApplicationConfig(DobPanel, 'funnel-components');
